import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../../styles/App.css";
import { getLastCommittedImport, revertImport, ImportApiError } from "../../api/importRating.js";
import { PERSON_GENDER_LABELS } from "../../decision/allocationLabels.js";
import { PRIVILEGE_OPTIONS } from "./PrivilegeSelect.js";
import { ZONE_OPTIONS } from "./DefaultZoneBlock.js";

// Відкат останнього зафіксованого імпорту: спершу прев'ю (GET
// /api/import/last-committed нічого не змінює), потім підтвердження і
// POST /api/import/rating/:id/revert.

const ACTIONS = {
  restore: { label: "повернуться дані до імпорту", badge: "повернення" },
  delete: { label: "буде видалено (створені імпортом)", badge: "видалення" },
  keep: { label: "залишаться (на них уже є посилання)", badge: "залишиться" },
  none: { label: "без змін", badge: "без змін" },
};

const KIND_LABELS = { rating: "Рейтинг (ППР)", students: "Список студентів" };

const FIELD_LABELS = {
  privilege_level: "Пільга",
  home_zone: "Віддаленість",
  gender: "Стать",
  course: "Курс",
  group_code: "Група",
  rating: "Рейтинг",
  room: "Кімната",
};

const optionLabel = (options, value) => options.find((o) => o.value === value)?.label ?? String(value);

function formatValue(field, value) {
  if (value === null || value === undefined || value === "") {
    return field === "room" ? "без кімнати" : "не вказано";
  }
  if (field === "privilege_level") return optionLabel(PRIVILEGE_OPTIONS, value);
  if (field === "home_zone") return optionLabel(ZONE_OPTIONS, value);
  if (field === "gender") return PERSON_GENDER_LABELS[value] || value;
  return String(value);
}

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString("uk-UA") : "—");
const formatDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("uk-UA") : "—");

function ChangesCell({ item }) {
  if (item.action === "delete") return <span>здобувача буде видалено</span>;
  if (item.action === "keep") return <span>здобувач лишиться в системі як є</span>;
  if (item.changes.length === 0) return <span>—</span>;
  return (
    <ul className="revert-changes">
      {item.changes.map((c) => (
        <li key={c.field}>
          <strong>{FIELD_LABELS[c.field] || c.field}:</strong> {formatValue(c.field, c.current)} →{" "}
          {formatValue(c.field, c.restored)}
        </li>
      ))}
    </ul>
  );
}

function ScoreCell({ score }) {
  if (!score) return <span>—</span>;
  return (
    <span>
      {score.ses} · {score.dorm} · {score.act}
      {score.confirmed && <span className="revert-confirmed"> (підтверджено)</span>}
    </span>
  );
}

const ImportRevertPage = () => {
  const [state, setState] = useState({ status: "loading", data: null, message: "" });
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    setState({ status: "loading", data: null, message: "" });
    return getLastCommittedImport()
      .then((data) => setState({ status: "done", data, message: "" }))
      .catch((err) =>
        setState({
          status: "error",
          data: null,
          message: err instanceof ImportApiError ? err.message : "Не вдалося завантажити дані імпорту.",
        })
      );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const batch = state.data?.batch || null;
  const summary = state.data?.summary;
  const items = useMemo(() => state.data?.items || [], [state.data]);
  const visible = useMemo(() => (filter ? items.filter((i) => i.action === filter) : items), [items, filter]);

  const handleRevert = async () => {
    const lines = [
      `Відкотити імпорт «${batch.sheetName}» (${formatDateTime(batch.committedAt)})?`,
      `Повернуться дані: ${summary.restore}; буде видалено: ${summary.delete}; залишиться: ${summary.keep}.`,
      `Буде видалено знімків балів: ${summary.scores}` +
        (summary.confirmedScores ? `, з них підтверджених: ${summary.confirmedScores}.` : "."),
      "Дію не можна скасувати.",
    ];
    if (!window.confirm(lines.join("\n"))) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await revertImport(batch.id);
      setResult({
        ok: true,
        text: `Імпорт відкочено: повернуто ${res.restored}, видалено ${res.deleted}, залишено ${res.skipped}.`,
      });
      await load();
    } catch (err) {
      setResult({
        ok: false,
        text: err instanceof ImportApiError ? `Помилка (${err.status}): ${err.message}` : "Не вдалося відкотити імпорт.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-container">
      <h3 className="text-header">Відкат останнього зафіксованого імпорту</h3>
      <p className="text-header">
        Нижче показано, що саме зміниться. Нічого не відкочується, доки ви не натиснете «Відкотити імпорт» і не
        підтвердите дію.
      </p>

      {result && (
        <p className="text-header" role="status" style={{ color: result.ok ? "var(--free-room)" : "var(--full-busy)" }}>
          {result.text}
        </p>
      )}

      {state.status === "loading" && <p className="text-header">Завантаження...</p>}
      {state.status === "error" && (
        <p className="text-header" style={{ color: "var(--full-busy)" }}>
          {state.message}
        </p>
      )}

      {state.status === "done" && !batch && (
        <p className="no-data">Немає зафіксованих імпортів — відкочувати нічого.</p>
      )}

      {state.status === "done" && batch && (
        <>
          <div className="input-component" data-testid="revert-batch">
            <div className="form-container stats-grid">
              <p className="input-block text-header">
                <strong>Тип:</strong> {KIND_LABELS[batch.kind] || batch.kind}
              </p>
              <p className="input-block text-header">
                <strong>Аркуш:</strong> {batch.sheetName}
              </p>
              <p className="input-block text-header">
                <strong>Гуртожиток:</strong> {batch.dormitoryName || "—"}
              </p>
              {batch.kind === "rating" && (
                <p className="input-block text-header">
                  <strong>Період:</strong> {formatDate(batch.periodStart)} — {formatDate(batch.periodEnd)}
                </p>
              )}
              <p className="input-block text-header">
                <strong>Зафіксовано:</strong> {formatDateTime(batch.committedAt)}
              </p>
              <p className="input-block text-header">
                <strong>Ким:</strong> {batch.createdBy || "—"}
              </p>
            </div>
          </div>

          <div className="form-container stats-grid" data-testid="revert-summary">
            <p className="input-block text-header">
              <strong>Повернуться дані:</strong> {summary.restore}
            </p>
            <p className="input-block text-header">
              <strong>Буде видалено:</strong> {summary.delete}
            </p>
            <p className="input-block text-header">
              <strong>Залишаться:</strong> {summary.keep}
            </p>
            <p className="input-block text-header">
              <strong>Знімків балів буде видалено:</strong> {summary.scores}
            </p>
          </div>
          {summary.confirmedScores > 0 && (
            <p className="text-header" style={{ color: "var(--full-busy)" }}>
              Увага: серед балів, що будуть видалені, підтверджених — {summary.confirmedScores}.
            </p>
          )}

          <div className="form-container">
            <div className="input-block text-header">
              <select
                className="header-content-select"
                aria-label="Фільтр за дією"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="">Усі рядки ({items.length})</option>
                {["restore", "delete", "keep"].map((a) => (
                  <option key={a} value={a}>
                    {ACTIONS[a].label} ({items.filter((i) => i.action === a).length})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="table-scroll">
            <table className="table import-table revert-table" data-testid="revert-table">
              <thead>
                <tr>
                  <th className="table-header">№</th>
                  <th className="table-header">ПІБ</th>
                  <th className="table-header">Дія</th>
                  <th className="table-header">Що зміниться</th>
                  <th className="table-header">Бали, що видаляться (с · г · д)</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr>
                    <td className="table-content" colSpan={5}>
                      Немає рядків у цій категорії.
                    </td>
                  </tr>
                )}
                {visible.map((item) => (
                  <tr key={item.rowNumber}>
                    <td className="table-content">{item.rowNumber}</td>
                    <td className="table-content cell-name">
                      {item.action === "delete" ? (
                        item.pib || "—"
                      ) : (
                        <Link to={`/student/${item.studentId}`} className="violation-link">
                          {item.pib || "—"}
                        </Link>
                      )}
                    </td>
                    <td className="table-content">
                      <span className={`revert-badge revert-badge-${item.action}`}>{ACTIONS[item.action]?.badge}</span>
                    </td>
                    <td className="table-content cell-issues">
                      <ChangesCell item={item} />
                    </td>
                    <td className="table-content">
                      <ScoreCell score={item.score} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button type="button" className="remove-button revert-button" disabled={busy} onClick={handleRevert}>
            {busy ? "Відкочую..." : "Відкотити імпорт"}
          </button>
        </>
      )}
    </div>
  );
};

export default ImportRevertPage;
